<template>
  <table class="run-policy__table">
    <thead>
      <tr>
        <th>{{ $t('paymentImportHistory.headers.dueDate') }}</th>
        <th>{{ $t('paymentImportHistory.headers.before') }}</th>
        <th>{{ $t('paymentImportHistory.headers.after') }}</th>
      </tr>
    </thead>
    <tbody>
      <!-- A schedule's table is mostly rows that did not move: redistribution touches every unpaid
           receipt, so the one the carrier actually collected has to be findable at a glance.
           Unchanged rows recede; on a changed row only the fields that moved are coloured. -->
      <tr v-for="pair in rows" :key="pair.key"
          :class="{
            'run-policy__row--changed': pair.changed,
            'run-policy__row--same': !pair.changed,
          }">
        <td>{{ formatDate(pair.dueDate) }}</td>
        <td>
          <template v-if="!pair.before">{{ $t('paymentImportHistory.receiptAbsent') }}</template>
          <template v-else>
            <span :class="{ 'run-diff--was': pair.moved.amount }">{{ formatMoney(pair.before.amount) }}</span>
            ·
            <span :class="{ 'run-diff--was': pair.moved.status }">
              {{ $t(`paymentImportHistory.receiptStatus.${pair.before.status}`) }}
            </span>
            <span v-if="collectedLabel(pair.before)" :class="{ 'run-diff--was': pair.moved.collected }">
              · {{ collectedLabel(pair.before) }}
            </span>
          </template>
        </td>
        <td>
          <template v-if="!pair.after">{{ $t('paymentImportHistory.receiptAbsent') }}</template>
          <template v-else>
            <span :class="{ 'run-diff--now': pair.moved.amount }">{{ formatMoney(pair.after.amount) }}</span>
            ·
            <span :class="{ 'run-diff--now': pair.moved.status }">
              {{ $t(`paymentImportHistory.receiptStatus.${pair.after.status}`) }}
            </span>
            <span v-if="collectedLabel(pair.after)" :class="{ 'run-diff--now': pair.moved.collected }">
              · {{ collectedLabel(pair.after) }}
            </span>
          </template>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import { getBrowserMoneyLocale } from '@/utils/amountFormatter';
import { diffPairs } from '@/utils/paymentDiff';

/**
 * A policy's instalments before and after, one line per instalment: Payments → Import history
 * (what a run did) and a carrier read's held policies (what Apply would do) read the same way.
 */
export default {
  name: 'PaymentDiffTable',
  props: {
    /** `{ key, dueDate, before, after }` — see utils/paymentDiff. */
    pairs: { type: Array, required: true },
  },
  computed: {
    rows() {
      return diffPairs(this.pairs);
    },
  },
  methods: {
    // The collection recorded against an instalment, or '' when it carries none.
    collectedLabel(entry) {
      if (!entry?.paidAmount) return '';
      return this.$t('paymentImportHistory.collected', {
        amount: this.formatMoney(entry.paidAmount),
        date: this.formatDate(entry.paidDate),
      });
    },
    formatMoney(val) {
      if (val === null || val === undefined || val === '') return '—';
      const num = Number(val);
      if (Number.isNaN(num)) return String(val);
      return new Intl.NumberFormat(getBrowserMoneyLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        .format(num);
    },
    formatDate(val) {
      return val ? String(val).slice(0, 10) : '—';
    },
  },
};
</script>

<style lang="scss">
.run-policy__table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;

  th, td {
    text-align: left;
    padding: 6px 8px;
    border-bottom: 1px solid var(--solid-lavender-grey);
  }

  th {
    color: var(--dark-purple);
    font-weight: 600;
  }
}

/* The receipts this run moved. Tinted so they are findable in a table of twelve. */
.run-policy__row--changed {
  background: #F3F0FF;
}

/* Everything redistribution left alone is context, and recedes. */
.run-policy__row--same {
  color: var(--dark-purple);
  opacity: 0.65;
}

/* What a field was: struck through, so the eye does not read it as current. */
.run-diff--was {
  color: var(--dark-purple);
  text-decoration: line-through;
}

/* What it is now. Bold, not green: green read as "good", and a changed value is not a verdict. */
.run-diff--now {
  color: var(--solid-black-purple);
  font-weight: 700;
}
</style>
